<template>
  <div :class="breadcrumbClasses">
    <slot name="breadcrumb" />
  </div>
  <router-view :page-id="props.pageId" />
  <customization-global-dialogs />
</template>

<script setup>
import {computed, watch, onMounted, ref} from 'vue';
import {useRoute, useRouter} from 'vue-router';
import {useStore} from 'vuex';
import CustomizationGlobalDialogs from '@/components/containers/buy-zone/customization/CustomizationGlobalDialogs.vue';
import {useCmsPage} from '@/composables/cms-page.js';
import {Url} from '@/utils/url';
import {Routes} from '@/constants/family-details.js';
import {FunctionalityTogglesApi} from '@/api/functionality-toggles.js';
import {FunctionalityToggle} from '@/constants/functionality-toggle.js';
import {scrollToTop} from '@/utils/scroll-to';

const props = defineProps({
  pageId: {
    type: String,
    default: '',
  },
});

const store = useStore();
const router = useRouter();

const cachedBuildId = computed(() => store.state.bikeCustomization.cachedBuildId);
const isElectraBrand = computed(() => store.state.backend.isElectraBrand);
const selectedModelId = computed(() => store.state.familyDetails.selectedModelId);
const selectedModel = computed(() => store.getters['familyDetails/selectedModel']);
const userParams = computed(() => store.getters['backend/occUrlParams'].userId);
const isLoading = ref(true);
const pageData = ref(null);
const route = useRoute();
const selections = computed(() => store.getters['bikeCustomization/selections']);

const isFDPCustomizationEnabled = ref(false);

watch(
  () => pageData.value,
  (newPageData) => {
    if (newPageData?.contentSlots?.contentSlot) {
      store.commit('headlessCms/setSlotData', newPageData.contentSlots.contentSlot);
    }
  },
  {immediate: true}
);
watch(
  () => isLoading.value,
  (newLoadingState) => {
    store.commit('headlessCms/setCMSLoadingState', newLoadingState);
  },
  {immediate: true}
);

onMounted(async () => {
  await checkFDPCustomizationToggle();
  try {
    if (route.params.modelCode) {
      store.commit('familyDetails/setSelectedModelId', route.params.modelCode);
    }
    store.commit('familyDetails/setHasErrorLoadingFamily', false);
    const familyId = route.params.familyId;
    await store.dispatch('familyDetails/getFamilyDetailsData', familyId);
    if (selectedModelId.value !== route.params.modelCode) {
      router.replace({
        name: route.name,
        params: {
          ...route.params,
          modelName: Url.encodeModelNametoUrl(selectedModel.value.name),
          modelCode: selectedModelId.value,
          sku: null,
        },
      });
    }

    const params = {productCode: selectedModelId.value};
    const cmsPage = await useCmsPage(props.pageId, userParams.value, params);
    pageData.value = cmsPage.pageData.value;
    isLoading.value = cmsPage.isLoading.value;

    // cachedBuildId exists if user is redirected to login page after attempting to save an existing build
    if (cachedBuildId.value) {
      await fetchCachedBuild();
      return;
    }

    if (route.name === Routes.FDP_CUSTOMIZED_BUILD) {
      await handleCustomizeRoute();
    }
  } catch (e) {
    console.error('Error loading family details:', e);
    store.commit('familyDetails/setHasErrorLoadingFamily', true);
  } finally {
    isLoading.value = false;
  }
});

async function checkFDPCustomizationToggle() {
  try {
    const toggle = await FunctionalityTogglesApi.getToggleById(FunctionalityToggle.FDP_CUSTOMIZATION_TOGGLE);
    isFDPCustomizationEnabled.value = toggle?.active;
  } catch (error) {
    console.error('Failed to fetch FDP Customization toggle configuration', error);
  }
}

async function fetchCachedBuild() {
  try {
    store.commit('bikeCustomization/fetchCachedBuildData');
    await store.dispatch('bikeCustomization/updateSelection', {selections: selections.value, module: 'components'});
    store.commit('productDetails/setIsCustomizationMode', true);
    store.commit('bikeCustomization/clearCachedBuildData');
  } catch (e) {
    console.error('Error fetching cached build:', e);
  }
}

async function handleCustomizeRoute() {
  const buildId = route.params.buildId;
  if (!buildId) {
    if (!isFDPCustomizationEnabled.value) {
      //redirect to old p1 configurator
      window.location.href = selectedModel.value?.projectOneUrl;
    }
    const startingPoint = selectedModel.value?.customProductStartingPoint;
    if (startingPoint) {
      await store.dispatch('bikeCustomization/fetchStartingCustomizationOptions', startingPoint);
    } else {
      rerouteToFDP();
    }
    return;
  }

  if (buildId && !buildId?.includes('P-')) {
    rerouteToFDP();
    return;
  }
  // Have to make this call after getFamilyDetailsData in order to have all surrounding components on FDP page
  await loadBikeConfiguration(buildId);
}

async function loadBikeConfiguration(buildId) {
  try {
    store.commit('familyDetails/setIsBuyZoneLoading', true);
    await store.dispatch('bikeCustomization/fetchBikeConfiguration', buildId);
    await store.dispatch('bikeCustomization/fetchSavedSelections', buildId);
  } catch (error) {
    rerouteToFDP();
    store.commit('bikeCustomization/setHasErrorLoadingSavedBuild', true);
  } finally {
    store.commit('familyDetails/setIsBuyZoneLoading', false);
  }
}

function rerouteToFDP() {
  console.error('Error loading bike configuration');
  store.commit('bikeCustomization/setHasErrorLoadingSavedBuild', true);

  router.replace({
    name: Routes.FAMILY_DETAILS_PAGE,
    params: {
      ...route.params,
      sku: null,
    },
  });
  scrollToTop();
}

const breadcrumbClasses = computed(() => ({
  'bg-gray-10': !isElectraBrand.value,
  'bg-gray-05': isElectraBrand.value,
}));
</script>
